import { QrCodeImage } from '@/components/hotel-access-codes/QrCodeImage';
import type { HotelAccessCode } from '@/config/objects/hotel-access-codes';
import {
  Button,
  EmptyTableState,
  RichTextContent,
  formatDateTime,
  toast,
} from '@startupquickstart/frontend';
import { Copy, Download, FileText } from 'lucide-react';
import { useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';

interface AccessCodeDetailsCardProps {
  accessCode: HotelAccessCode;
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

function Field({ label, children }: FieldProps) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1">
        {label}
      </div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

/**
 * Details card for an access code: the scannable/downloadable QR, scan URL,
 * status, validity, and description. The raw code is intentionally not shown
 * (it's encoded in the QR / scan URL). Copy URL and Download PNG live in the
 * card header. The system/audit trail is omitted to keep the view focused.
 *
 * @param accessCode - The access code to render details for.
 * @returns The details card.
 */
export function AccessCodeDetailsCard({ accessCode }: AccessCodeDetailsCardProps) {
  const qrRef = useRef<HTMLDivElement>(null);
  const scanUrl = `${window.location.origin}/api/v1/scans/${accessCode.code}`;

  const handleCopyUrl = useCallback(() => {
    navigator.clipboard.writeText(scanUrl);
    toast.success('Scan URL copied to clipboard');
  }, [scanUrl]);

  const handleDownloadPng = useCallback(() => {
    const img = qrRef.current?.querySelector('img');
    if (!img) return;
    const link = document.createElement('a');
    link.download = `qr-code-${accessCode.code}.png`;
    link.href = img.src;
    link.click();
  }, [accessCode.code]);

  return (
    <div className="rounded-xl border bg-card shadow-sm">
      <div className="px-6 py-4 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium tracking-tight text-zinc-900">Details</h2>
          <p className="text-sm text-muted-foreground">
            Scan or share this code to check in guests
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={handleCopyUrl} className="cursor-pointer">
            <Copy className="h-4 w-4 mr-2" />
            Copy URL
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadPng}
            className="cursor-pointer"
          >
            <Download className="h-4 w-4 mr-2" />
            Download PNG
          </Button>
        </div>
      </div>

      <div className="p-6">
        <div className="flex flex-col md:flex-row gap-6">
          <div
            ref={qrRef}
            className="bg-white p-3 rounded-lg border flex-shrink-0 self-start"
          >
            <QrCodeImage
              value={scanUrl}
              size={160}
              level="H"
              alt={`QR code for ${accessCode.name}`}
            />
          </div>

          <div className="flex-1 min-w-0 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label="Hotel">
                {accessCode.hotel ? (
                  <Link
                    to={`/hotels/${accessCode.hotel.id}`}
                    className="text-primary hover:underline"
                  >
                    {accessCode.hotel.name}
                  </Link>
                ) : (
                  <span className="text-muted-foreground">N/A</span>
                )}
              </Field>

              <Field label="Status">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                    accessCode.isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-red-50 text-red-700'
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      accessCode.isActive ? 'bg-emerald-500' : 'bg-red-500'
                    }`}
                  />
                  {accessCode.isActive ? 'Active' : 'Inactive'}
                </span>
              </Field>

              <Field label="Duration">
                {accessCode.durationInDays != null ? (
                  `${accessCode.durationInDays} day${accessCode.durationInDays === 1 ? '' : 's'}`
                ) : (
                  <span className="text-muted-foreground">No duration set</span>
                )}
              </Field>

              <Field label="Expires At">
                {formatDateTime(accessCode.expiresAt) || (
                  <span className="text-muted-foreground">No expiration</span>
                )}
              </Field>

              {accessCode.revokedAt && (
                <Field label="Revoked At">
                  <span className="text-destructive font-medium">
                    {formatDateTime(accessCode.revokedAt)}
                  </span>
                </Field>
              )}
            </div>

            <Field label="Scan URL">
              <code className="text-sm bg-muted px-2 py-1 rounded block break-all whitespace-normal">
                {scanUrl}
              </code>
            </Field>
          </div>
        </div>

        <div className="border-t mt-6 pt-6">
          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
            Description
          </div>
          {accessCode.description ? (
            <RichTextContent content={accessCode.description} />
          ) : (
            <EmptyTableState
              icon={<FileText className="h-6 w-6" />}
              title="No description"
              description="Edit to add a description"
            />
          )}
        </div>
      </div>
    </div>
  );
}
